import {Component, ElementRef, OnInit, Renderer2, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from './services/user.service';
import {HasSubscriptions} from './components/has-subscriptions';
import {Event, NavigationEnd, Router} from '@angular/router';
import {CommunicationService} from './services/communication.service';
import {DrawerModes, ListingCategory, SCREEN_SIZE, ViewModes} from './classes/enums';
import {DrawerComponent} from './components/drawer/drawer.component';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {PreferencesService} from './services/preferences.service';
import {Filters} from './classes/user-preferences';
import {LanguageService} from './services/language/language.service';
import {ResizeService} from './services/resize/resize.service';
import {StyleService} from './services/style/style.service';
import {DeviceService} from './services/device/device.service';
import {AnalyticsService} from './services/analytics/analytics.service';
import { filter } from 'rxjs/operators';
import {DomManipulationService} from './services/dom-manipulation/dom-manipulation.service';
import { MetaTagService } from './services/meta-tag.service';
import { Helper } from './classes/helper';
import { RouteCode } from './classes/route-code';

declare var require: any;
const objectFitImages = require('object-fit-images');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['app.component.scss'],
  animations: [
    trigger('flipState', [
      state('active', style({
        transform: 'rotateY(90deg)'
      })),
      state('animating', style({
        transform: 'rotateY(90deg)'
      })),
      state('inactive', style({
        transform: 'none'
      })),
      transition('active => inactive', animate('500ms ease-out')),
      transition('inactive => animating', animate('250ms ease-in')),
      transition('animating => inactive', animate('250ms ease-in'))
    ]),
  ]
})
export class AppComponent extends HasSubscriptions implements OnInit {
  removePadding: boolean;
  removeFullHeight: boolean;
  screenSize: SCREEN_SIZE;
  isLoggedIn: boolean = true;
  showSignIn: boolean = false;
  isPopupVisible: boolean = false;
  showSearch: boolean = false;
  showLeftNav: boolean = false;
  showRightNav: boolean = false;
  showQuebecModal: boolean = false;
  flip: string = 'inactive';
  currentShownListingType: ListingCategory = ListingCategory.RESIDENTIAL;
  static screenSize;
  activeViewMode: ViewModes = ViewModes.MAP;
  @ViewChild('appContainer') appContainer: ElementRef;

  @ViewChild('drawer') drawer: DrawerComponent;

  concealHeader: boolean = false;
  concealFooter: boolean = false;
  currentPage: string;
  activeRouteCode: RouteCode;
  provinces: string[] = ['ab', 'bc', 'mb', 'nb', 'nl', 'nt', 'ns', 'nu', 'on', 'pe', 'qc',  'sk', 'yt'];

  SCREEN_SIZE = SCREEN_SIZE;
  ViewMode = ViewModes;
  ListingCategory = ListingCategory;
  // Only for when they have the right nav open, we need to know the last page they were on
  private _previousPage: string;

  constructor(private translate: TranslateService,
    private userService: UserService,
    private preferencesService: PreferencesService,
    private router: Router,
    private communicationService: CommunicationService,
    private renderer: Renderer2,
    private languageService: LanguageService,
    private resizeService: ResizeService,
    private styleService: StyleService,
    private deviceService: DeviceService,
    private domManipulationService: DomManipulationService,
    private analyticsService: AnalyticsService, // must be injected at app.component level to initialize Angulartics2
    private metaTagService: MetaTagService
  ) {
    super();
  }

  ngOnInit() {
    this.translate.addLangs(['en', 'fr']);
    this.translate.setDefaultLang('en');

    // Check if the user has a valid access token, and try to get a new one if they don't.
    this.userService.checkIfAlreadyLoggedIn();
    /* //Uncomment to turn on dumb login again
    this.isLoggedIn = this.isLoggedIn || this.prerenderService.isServerMode;

    this.route.queryParams.subscribe(queryParams => {
      const password = queryParams['password'];
      this.isLoggedIn = this.isLoggedIn || (!!password && password === SimpleLoginComponent.getPassword());// || (<any>environment).isLocal;
    });*/

    // If desktop, automatically enable notifications
    if (!this.deviceService.isMobileDevice()) { this.communicationService.notifications.enableNotifications.next(true); }

    this.addSub(this.communicationService.screenSize.subscribe((screenSize) => {
      AppComponent.screenSize = screenSize;
    }));

    this.addSub(this.communicationService.navigation.showSearch.subscribe((showSearch) => {
      this.showSearch = showSearch;
    }));

    this.addSub(this.communicationService.screenSize.subscribe((screenSize) => {
      this.screenSize = screenSize;
      this.removeBodyPadding();
    }));

    this.addSub(this.communicationService.activeRouteCode.subscribe((activeRouteCode) => {
      this.activeRouteCode = activeRouteCode;
      this.removeBodyPadding();
    }));

    this.addSub(this.preferencesService.localeChanged.subscribe(locale => {
      this.languageService.setCurrentLanguage(locale);
    }));

    if (this.screenSize === SCREEN_SIZE.MOBILE_ONLY) {
      this.translate.get('notification.searching-for-location').toPromise().then((res: string) => {
        this.communicationService.notifications.notificationChanged.next(res);
      })
    }

    this.addSub(this.preferencesService.viewModesChanged.subscribe((s) => {
      this.activeViewMode = s;
      this.concealFooter = this._hideFooter();
    }));

    this.addSub(this.router.events
      .pipe(filter((e: Event) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this.currentPage = e.url;
        this._previousPage = this.currentPage;
        this.concealFooter = this._hideFooter();
        this.concealHeader = this._hideHeader(e);
        // Make sure to close the nav anytime you move
        if (this.screenSize === SCREEN_SIZE.MOBILE_ONLY) {
          this.communicationService.navigation.toggleNavs.next({ left: false, right: false });
        }

        this.removeFullHeight = this.activeRouteCode !== RouteCode.SEARCH_LISTING_PAGE;

        // Set the Open Graph Tag og:image to be the RE/MAX URL. If a page needs it set differently, that will be handled in the page itself
        // We need to user the custom _og images here since Open Graph Images have minimum size requirements
        const imageURL = Helper.getStaticPathForResource(this.currentShownListingType === ListingCategory.COMMERCIAL ? '/assets/REMAX_Logo/REMAX_Commercial_og.png'
          : (this.currentShownListingType === ListingCategory.LUXURY ? '/assets/REMAX_Logo/REMAX_Collection_og.png'
            : '/assets/REMAX_Logo/REMAX_Residential_og.png'));

        this.metaTagService.setOpenGraphTags([
          {
            name: 'image',
            content: Helper.getStaticPathForResource(imageURL)
          },
          {
            name: 'title',
            content: 'RE/MAX',
          },
          {
            name: 'description',
            content: 'RE/MAX'
          },
          {
            name: 'url',
            content: this.router.url
          },
          {
            name: 'details',
            content: ''
          },
          {
            name: 'latitude',
            content: '',
            prefixName: 'place:location'
          },
          {
            name: 'longitude',
            content: '',
            prefixName: 'place:location'
          },
          {
            name: 'type',
            content: 'website'
          }
        ]);
      }));

    // Listen to any resize events on the window
    this.addSub(this.resizeService.onResize().subscribe(() => this.communicationService.screenSize.next(this.resizeService.getMediaQuerySize())));

    this.addSub(this.communicationService.showSignIn.subscribe((show) => {
      this.showSignIn = show;
      this.toggleScroll(show);
    }));

    this.addSub(this.communicationService.toolbar.filtersRequested.subscribe(() => this.drawer.openDrawer(DrawerModes.FILTERS)));

    this.addSub(this.communicationService.toolbar.searchFilters.subscribe(() => this.drawer.openDrawer(DrawerModes.SEARCH_FILTERS)));

    this.addSub(this.communicationService.toolbar.saveSearchRequested.subscribe(() => this.drawer.openDrawer(DrawerModes.SAVE_SEARCH)));

    this.addSub(this.communicationService.navigation.toggleNavs.subscribe((s) => {
      // right nav only slides in if you are on mobile
      if (AppComponent.screenSize < SCREEN_SIZE.DESKTOP_UP) {
        this.showRightNav = s.right;
      } else {
        if (!s.right && this.isPopupVisible) {
          // hide popup
          this.router.navigate(['/', { outlets: { popup: null } }]);
        }
      }

      this.showLeftNav = s.left;

      // When they user clicks on the account icon, we update the icon to be highlighted
      if (this.showRightNav) {
        this._previousPage = this.currentPage;
        this.currentPage = '/my-account';
      }
      else this.currentPage = this._previousPage;

      // Need this to prevent scrolling when side nav is enabled
      // TODO SSR
      this.toggleScroll(s.left || s.right);
    }));

    this.addSub(this.communicationService.flipPage.subscribe(() => this.flip = this.flip === 'inactive' ? 'animating' : this.flip));

    this.addSub(this.communicationService.listings.currentShownListingType.subscribe((s) => {
      // We reset the Filters every time its changed
      if (s !== this.currentShownListingType) this.preferencesService.updateFilters(new Filters());
      this.currentShownListingType = s;

    }));

    this.addSub(this.communicationService.notifications.showQuebecModal.subscribe((show) => {
      this.showQuebecModal = show;
      this.toggleScroll(show);
    }));

    objectFitImages();
  }

  private toggleScroll(show: boolean) {
    // Need this to prevent scrolling when side nav is enabled
    // TODO refactor this 2 classes below into one
    if (show) {
      this.styleService.addClass(this.renderer, 'html', 'with-side-nav');
    } else {
      this.styleService.removeClass(this.renderer, 'html', 'with-side-nav');
      this.styleService.removeClass(this.renderer, 'html', 'no-scroll');
    }
  }

  animationCallback(event) {
    if (event.fromState === 'inactive' && event.toState === 'animating') {
      this.flip = 'inactive';
    }
  }

  onRouteChange() {
    this.communicationService.navigation.toggleNavs.next({ left: false, right: false });
  }

  onShowPopup($event) {
    this.isPopupVisible = true;
  }

  onHidePopup($event) {
    this.isPopupVisible = false;
  }

  private _hideHeader(e): boolean {
    // return false;
    return e.url.startsWith('/reset-password');
  }

  private _hideFooter(): boolean {
    return this.activeRouteCode === RouteCode.SEARCH_LISTING_PAGE && this.activeViewMode !== ViewModes.GALLERY;
  }

  onLogin(login: boolean) {
    // if we don't wait the next tick to change isLoggedIn angular will throw a ExpressionChangedAfterItHasBeenCheckedError
    this.domManipulationService.runFunctionWithTimeout(() => {
      this.isLoggedIn = login;
    }, 1);
  }

  removeBodyPadding() {
    this.removePadding = this.activeRouteCode === RouteCode.SEARCH_LISTING_PAGE && this.screenSize <= SCREEN_SIZE.TABLET_LANDSCAPE_UP;
  }
}
